import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './PersonalDetailsPage.css';

const PersonalDetailsPage = () => {
    const { currentUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('profile');
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const fileInputRef = useRef(null);

    // Profile form state
    const [profile, setProfile] = useState({
        name: '',
        email: '',
        phone: '',
        bio: '',
        gender: '',
        dob: '',
    });

    // Password form state
    const [pwForm, setPwForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [pwLoading, setPwLoading] = useState(false);
    const [avatarPreview, setAvatarPreview] = useState(null);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!currentUser) { setLoading(false); return; }
            try {
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                const data = userDoc.exists() ? userDoc.data() : {};
                setProfile({
                    name: currentUser.displayName || data.name || '',
                    email: currentUser.email || '',
                    phone: data.phone || '',
                    bio: data.bio || '',
                    gender: data.gender || '',
                    dob: data.dob || '',
                });
                setAvatarPreview(currentUser.photoURL || null);
            } catch (err) {
                console.error(err);
            }
            setLoading(false);
        };
        fetchUserData();
    }, [currentUser]);

    const showSuccess = (msg) => {
        setSuccessMsg(msg);
        setErrorMsg('');
        setTimeout(() => setSuccessMsg(''), 4000);
    };

    const showError = (msg) => {
        setErrorMsg(msg);
        setSuccessMsg('');
        setTimeout(() => setErrorMsg(''), 5000);
    };

    const handleProfileSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Update Firebase Auth display name
            await updateProfile(auth.currentUser, { displayName: profile.name });

            // Update Firestore
            await setDoc(doc(db, 'users', currentUser.uid), {
                name: profile.name,
                email: profile.email,
                phone: profile.phone,
                bio: profile.bio,
                gender: profile.gender,
                dob: profile.dob,
                updatedAt: new Date().toISOString(),
            }, { merge: true });

            showSuccess('Profile updated successfully!');
        } catch (err) {
            showError('Failed to update profile: ' + err.message);
        }
        setSaving(false);
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (pwForm.newPassword !== pwForm.confirmPassword) {
            showError('New passwords do not match.');
            return;
        }
        if (pwForm.newPassword.length < 6) {
            showError('Password must be at least 6 characters.');
            return;
        }
        setPwLoading(true);
        try {
            const credential = EmailAuthProvider.credential(currentUser.email, pwForm.currentPassword);
            await reauthenticateWithCredential(auth.currentUser, credential);
            await updatePassword(auth.currentUser, pwForm.newPassword);
            setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
            showSuccess('Password changed successfully!');
        } catch (err) {
            showError(err.code === 'auth/wrong-password' ? 'Incorrect current password.' : 'Failed to change password: ' + err.message);
        }
        setPwLoading(false);
    };

    const handleAvatarClick = () => fileInputRef.current?.click();

    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            showError('Image must be smaller than 2MB.');
            return;
        }
        const reader = new FileReader();
        reader.onload = async (ev) => {
            const dataUrl = ev.target.result;
            setAvatarPreview(dataUrl);
            try {
                await updateProfile(auth.currentUser, { photoURL: dataUrl });
                await setDoc(doc(db, 'users', currentUser.uid), { photoURL: dataUrl }, { merge: true });
                showSuccess('Profile photo updated!');
            } catch (err) {
                showError('Could not update photo.');
            }
        };
        reader.readAsDataURL(file);
    };

    const getInitials = (name, email) => {
        if (name) return name.charAt(0).toUpperCase();
        if (email) return email.charAt(0).toUpperCase();
        return 'U';
    };

    const isGoogleUser = currentUser?.providerData[0]?.providerId === 'google.com';

    if (!currentUser) {
        return (
            <div className="pd-page">
                <div className="pd-container">
                    <div className="pd-login-prompt">
                        <div className="pd-lock-icon">🔒</div>
                        <h2>Login Required</h2>
                        <p>Please login to view your personal details.</p>
                        <Link to="/login" className="pd-btn pd-btn-primary">Login Now</Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pd-page">
            <div className="pd-container">

                {/* Alerts */}
                {successMsg && (
                    <div className="pd-alert pd-alert-success">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                        {successMsg}
                    </div>
                )}
                {errorMsg && (
                    <div className="pd-alert pd-alert-error">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                        {errorMsg}
                    </div>
                )}

                {/* Hero Profile Card */}
                <div className="pd-hero">
                    <div className="pd-avatar-ring" onClick={handleAvatarClick} title="Change photo">
                        {avatarPreview ? (
                            <img src={avatarPreview} alt="Profile" className="pd-avatar-img" />
                        ) : (
                            <div className="pd-avatar-initials">{getInitials(profile.name, profile.email)}</div>
                        )}
                        <div className="pd-avatar-overlay">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                                <circle cx="12" cy="13" r="4" />
                            </svg>
                        </div>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />

                    <div className="pd-hero-info">
                        <h2 className="pd-hero-name">{profile.name || 'Your Name'}</h2>
                        <p className="pd-hero-email">{profile.email}</p>
                        <div className="pd-hero-badges">
                            <span className="pd-badge pd-badge-method">
                                {isGoogleUser ? 'Google Account' : 'Email Account'}
                            </span>
                            <span className={`pd-badge ${currentUser.emailVerified ? 'pd-badge-verified' : 'pd-badge-unverified'}`}>
                                {currentUser.emailVerified ? '✓ Verified' : '✗ Unverified'}
                            </span>
                            {currentUser.metadata?.creationTime && (
                                <span className="pd-badge pd-badge-since">
                                    Since {new Date(currentUser.metadata.creationTime).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="pd-loading">
                        <div className="pd-spinner-lg"></div>
                        <p>Loading your profile...</p>
                    </div>
                ) : (
                    <>
                        {/* Tab Switcher */}
                        {!isGoogleUser && (
                            <div className="pd-tab-switcher">
                                <button
                                    className={`pd-tab-btn ${activeTab === 'profile' ? 'pd-tab-active' : ''}`}
                                    onClick={() => setActiveTab('profile')}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
                                    Profile
                                </button>
                                <button
                                    className={`pd-tab-btn ${activeTab === 'security' ? 'pd-tab-active' : ''}`}
                                    onClick={() => setActiveTab('security')}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                                    Security
                                </button>
                            </div>
                        )}

                        {/* Form Card */}
                        <div className="pd-card">
                            {/* Profile Tab */}
                            {activeTab === 'profile' && (
                                <form className="pd-form" onSubmit={handleProfileSave}>
                                    <div className="pd-section-header">
                                        <div className="pd-section-icon">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
                                        </div>
                                        <div>
                                            <h3 className="pd-section-title">Profile Information</h3>
                                            <p className="pd-section-desc">Update your personal info below</p>
                                        </div>
                                    </div>

                                    <div className="pd-grid-2">
                                        <div className="pd-field">
                                            <label>Full Name</label>
                                            <input
                                                type="text"
                                                value={profile.name}
                                                onChange={e => setProfile({ ...profile, name: e.target.value })}
                                                placeholder="Enter your full name"
                                            />
                                        </div>
                                        <div className="pd-field">
                                            <label>Phone Number</label>
                                            <input
                                                type="tel"
                                                value={profile.phone}
                                                onChange={e => setProfile({ ...profile, phone: e.target.value })}
                                                placeholder="+91 98765 43210"
                                                maxLength={15}
                                            />
                                        </div>
                                        <div className="pd-field">
                                            <label>
                                                Email Address
                                                <span className="pd-readonly-badge">Read-only</span>
                                            </label>
                                            <input type="email" value={profile.email} disabled className="pd-readonly" />
                                        </div>
                                        <div className="pd-field">
                                            <label>Date of Birth</label>
                                            <input
                                                type="date"
                                                value={profile.dob}
                                                onChange={e => setProfile({ ...profile, dob: e.target.value })}
                                            />
                                        </div>
                                        <div className="pd-field">
                                            <label>Gender</label>
                                            <select value={profile.gender} onChange={e => setProfile({ ...profile, gender: e.target.value })}>
                                                <option value="">Select gender</option>
                                                <option value="male">Male</option>
                                                <option value="female">Female</option>
                                                <option value="non-binary">Non-binary</option>
                                                <option value="prefer-not">Prefer not to say</option>
                                            </select>
                                        </div>
                                    </div>

                                    <div className="pd-field">
                                        <label>Bio</label>
                                        <textarea
                                            value={profile.bio}
                                            onChange={e => setProfile({ ...profile, bio: e.target.value })}
                                            placeholder="Tell us a little about yourself..."
                                            rows={3}
                                            maxLength={200}
                                        />
                                        <span className="pd-char-count">{profile.bio.length}/200</span>
                                    </div>

                                    <div className="pd-form-actions">
                                        <button type="submit" className="pd-btn pd-btn-primary" disabled={saving}>
                                            {saving ? (
                                                <><span className="pd-spinner"></span> Saving...</>
                                            ) : (
                                                <>
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                                                    Save Changes
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}

                            {/* Security Tab */}
                            {activeTab === 'security' && (
                                <form className="pd-form" onSubmit={handlePasswordChange}>
                                    <div className="pd-section-header">
                                        <div className="pd-section-icon pd-section-icon-lock">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                                        </div>
                                        <div>
                                            <h3 className="pd-section-title">Change Password</h3>
                                            <p className="pd-section-desc">Keep your account secure with a strong password</p>
                                        </div>
                                    </div>

                                    <div className="pd-field">
                                        <label>Current Password</label>
                                        <input
                                            type="password"
                                            value={pwForm.currentPassword}
                                            onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })}
                                            placeholder="Enter current password"
                                            required
                                        />
                                    </div>
                                    <div className="pd-grid-2">
                                        <div className="pd-field">
                                            <label>New Password</label>
                                            <input
                                                type="password"
                                                value={pwForm.newPassword}
                                                onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                                                placeholder="Min. 6 characters"
                                                required
                                                minLength={6}
                                            />
                                        </div>
                                        <div className="pd-field">
                                            <label>Confirm New Password</label>
                                            <input
                                                type="password"
                                                value={pwForm.confirmPassword}
                                                onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                                                placeholder="Re-enter new password"
                                                required
                                            />
                                        </div>
                                    </div>
                                    {pwForm.newPassword && pwForm.confirmPassword && (
                                        <p className={`pd-pw-match ${pwForm.newPassword === pwForm.confirmPassword ? 'match' : 'no-match'}`}>
                                            {pwForm.newPassword === pwForm.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                                        </p>
                                    )}

                                    <div className="pd-form-actions">
                                        <button type="submit" className="pd-btn pd-btn-primary" disabled={pwLoading}>
                                            {pwLoading ? (
                                                <><span className="pd-spinner"></span> Updating...</>
                                            ) : (
                                                <>
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0110 0v4" /></svg>
                                                    Update Password
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PersonalDetailsPage;
